import { useState } from 'react';

import { Pen } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from '../../store';

import AppModal from '../../ui-components/AppModal';

import { SubSideBarProps } from './model';
import { useUpdateSettingMutation } from './settingsApi';

const SubSideBar = ({
  groupTypeArr,
  selectedGroup,
  onSelect,
}: SubSideBarProps) => {
  const { category: name } = useParams();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSettings, setSelectedSettings] = useState<any[]>([]);

  const [updateSetting] = useUpdateSettingMutation();

  const settings = useSelector((store: RootState) => {
    const typeKey = name?.toLowerCase() as keyof RootState['settings'];
    return store.settings[typeKey] ?? [];
  });

  const handleEditClick = (group: string) => {
    onSelect(group);
    const groupSettings = settings.filter(
      (item: any) => item.groupType === group
    );
    if (groupSettings.length > 0) {
      setSelectedSettings(groupSettings);
      setEditModalOpen(true);
    }
  };

  const handleFormChange = (index: number, value: string) => {
    setSelectedSettings((prev) =>
      prev.map((setting, i) => (i === index ? { ...setting, value } : setting))
    );
  };

  const handleFormSubmit = async () => {
    const payload: Record<string, string> = {};
    selectedSettings.forEach((setting) => {
      if (setting.name && setting.value !== undefined) {
        payload[setting.name] = setting.value;
      }
    });

    try {
      await updateSetting({ setting: payload }).unwrap();
    } catch (error: unknown) {
      console.error('Update error:', error);
    } finally {
      setEditModalOpen(false);
    }
  };

  return (
    <>
      <div className="w-70 h-100 bg-white rounded-lg shadow-md p-4 space-y-2 overflow-auto">
        {groupTypeArr.map((group) => (
          <button
            key={group}
            type="button"
            className={`w-full flex items-center justify-between text-left px-4 py-2 rounded-md transition cursor-pointer
      ${
        selectedGroup === group
          ? 'bg-brand-100 text-brand-700 font-medium'
          : 'hover:bg-gray-100 text-gray-700'
      }`}
            onClick={() => onSelect(group)}
          >
            <span>{group}</span>
            <span>
              <button
                type="button"
                className="border border-brand-500 rounded p-1 hover:bg-brand-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(group);
                }}
              >
                <Pen size={20} className="text-brand-500" />
              </button>
            </span>
          </button>
        ))}
      </div>

      <AppModal
        isOpen={editModalOpen}
        title="App Settings"
        onClose={() => setEditModalOpen(false)}
        formId="edit-settings-form"
        actions={[
          {
            label: 'Update',
            type: 'submit',
            variant: 'primary',
          },
          {
            label: 'Cancel',
            type: 'button',
            variant: 'secondary',
            onClick: () => setEditModalOpen(false),
          },
        ]}
      >
        <form
          id="edit-settings-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleFormSubmit();
          }}
          className="space-y-5"
        >
          {selectedSettings.map((setting, index) => (
            <div key={setting.name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {setting.description}
              </label>

              {setting.displayType === 'select' &&
              Array.isArray(setting.sourceValue) &&
              setting.sourceValue.length > 0 ? (
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  value={setting.value}
                  onChange={(e) => handleFormChange(index, e.target.value)}
                  autoFocus={index === 0}
                >
                  {setting.sourceValue.map((opt: any) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  value={setting.value}
                  onChange={(e) => handleFormChange(index, e.target.value)}
                  autoFocus={index === 0}
                />
              )}
            </div>
          ))}
        </form>
      </AppModal>
    </>
  );
};

export default SubSideBar;
